// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — action schemas (Tier 1 + Tier 2 + Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Action catalogue — discriminated by `kind` same as parameters. Each action
// references exactly one driving parameter (via `parameterId`) and a list of
// entity ids in the block definition that the action transforms
// (`selectionEntityIds`).
//
// Tier 1: move, stretch, flip
// Tier 2: rotate, scale, array, lookup
// Tier 3: polar-stretch (combined rotate + translate + stretch)
//
// Action/parameter kind compatibility is validated in
// validateDynamicMetadata (see dynamicBlockMetadata.ts). The evaluator
// (packages/block-evaluator) dispatches on `kind` and reads the action's
// own fields plus the driving parameter's runtime value.

import { z } from "zod";

import { baseActionSchema, drawingPointSchema } from "./dynamicBlockCommon";

// ═══════════════════════════════════════════════════════════════════════════
// MOVE — linear translation proportional to parameter delta
// ═══════════════════════════════════════════════════════════════════════════
//
// Works with linear / polar / xy parameters.
//   - distanceMultiplier: scalar applied to the parameter delta.
//   - angleOffset: degrees added to the parameter direction (CCW).
export const moveActionSchema = baseActionSchema.extend({
  kind: z.literal("move"),
  distanceMultiplier: z.number().default(1),
  angleOffset: z.number().default(0),
});
export type MoveAction = z.infer<typeof moveActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// STRETCH — crossing-frame translation
// ═══════════════════════════════════════════════════════════════════════════
//
// Same delta computation as Move, but vertices are only translated when
// they fall inside `crossingFrame` (AutoCAD STRETCH semantics).
//   - stretchDirection: degrees override for the parameter angle. 0 means
//     "use the parameter's own direction".
export const stretchActionSchema = baseActionSchema.extend({
  kind: z.literal("stretch"),
  distanceMultiplier: z.number().default(1),
  angleOffset: z.number().default(0),
  crossingFrame: z.object({
    corner1: drawingPointSchema,
    corner2: drawingPointSchema,
  }),
  stretchDirection: z.number().default(0),
});
export type StretchAction = z.infer<typeof stretchActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// FLIP — mirror across parameter axis when parameter.currentValue is true
// ═══════════════════════════════════════════════════════════════════════════
export const flipActionSchema = baseActionSchema.extend({
  kind: z.literal("flip"),
});
export type FlipAction = z.infer<typeof flipActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ROTATE — pivot selection around rotation-parameter basePoint (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Delta-angle is `(rotation.currentAngle - rotation.defaultAngle) *
// angleMultiplier` and the rotation happens around the parameter's
// basePoint. Requires parameter.kind === "rotation".
export const rotateActionSchema = baseActionSchema.extend({
  kind: z.literal("rotate"),
  angleMultiplier: z.number().default(1),
});
export type RotateAction = z.infer<typeof rotateActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SCALE — linear / polar / xy-driven scaling (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Scale factor = (currentDistance / defaultDistance) * scaleMultiplier for
// linear / polar. For xy, sx and sy can come independently from the X / Y
// currents (see evaluator). `scaleType` controls which axis actually scales:
//   - "uniform": sx = sy = factor
//   - "x-only":  sx = factor, sy = 1
//   - "y-only":  sx = 1,      sy = factor
export const scaleActionSchema = baseActionSchema.extend({
  kind: z.literal("scale"),
  scaleType: z.enum(["uniform", "x-only", "y-only"]).default("uniform"),
  scaleMultiplier: z.number().default(1),
});
export type ScaleAction = z.infer<typeof scaleActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ARRAY — distribute copies of the selection (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Linear parameter → N copies along parameter axis, spacing columnSpacing.
// Polar parameter  → radial (angular) distribution, spacing in angle.
// XY parameter     → 2D grid columns × rows from columnSpacing / rowSpacing.
//
// AutoCAD computes the instance count as floor(distance / spacing) + 1.
// Spacing 0 means "no copies" — action is a no-op.
export const arrayActionSchema = baseActionSchema.extend({
  kind: z.literal("array"),
  columnSpacing: z.number().default(0),
  rowSpacing: z.number().default(0),
});
export type ArrayAction = z.infer<typeof arrayActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// LOOKUP — cascade parameter updates via a lookup table (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Not a geometry transformation — instead, when the driving Lookup
// Parameter's current row changes, the evaluator applies the row's input
// values to the OTHER parameters listed in the table's input columns. The
// selection list is typically empty; the Lookup action itself does not
// touch entities.
export const lookupActionSchema = baseActionSchema.extend({
  kind: z.literal("lookup"),
});
export type LookupAction = z.infer<typeof lookupActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// POLAR STRETCH — combined rotate + translate + stretch (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's Polar Stretch Action is authored on a Polar Parameter and
// performs THREE transformations in sequence on vertices inside its
// `crossingFrame`:
//   1. Rotate by (polar.currentAngle − polar.defaultAngle) around the
//      polar parameter's base point (angleOffset in degrees adds to this).
//   2. Translate by (polar.currentDistance − polar.defaultDistance) along
//      the polar direction (multiplied by distanceMultiplier).
//   3. The full transformation is applied only to vertices that fall
//      inside `crossingFrame` (same STRETCH semantics as the classic
//      Stretch action — vertices outside the frame stay put).
//
// Required: parameter.kind === "polar". The evaluator rejects other kinds.
export const polarStretchActionSchema = baseActionSchema.extend({
  kind: z.literal("polar-stretch"),
  distanceMultiplier: z.number().default(1),
  angleOffset: z.number().default(0),
  crossingFrame: z.object({
    corner1: drawingPointSchema,
    corner2: drawingPointSchema,
  }),
});
export type PolarStretchAction = z.infer<typeof polarStretchActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Action discriminated union
// ═══════════════════════════════════════════════════════════════════════════
export const dynamicActionSchema = z.discriminatedUnion("kind", [
  moveActionSchema,
  stretchActionSchema,
  flipActionSchema,
  rotateActionSchema,
  scaleActionSchema,
  arrayActionSchema,
  lookupActionSchema,
  polarStretchActionSchema,
]);
export type DynamicAction = z.infer<typeof dynamicActionSchema>;
